import { useContext, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Sparkles, X, ArrowUpRight } from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import { hasRole } from "../../rbac/access";
import EmployeesPage from "./EmployeesPage";
import { PEOPLE_MODULES, PEOPLE_PATHS } from "./peopleModules";
import "../../styles/peopleHub.css";

const GUIDE_KEY = "people_hub_guide_dismissed";

function ModuleCard({ mod, active }) {
  const Icon = mod.icon;
  const opensFullPage = mod.id !== "employees";
  return (
    <Link
      to={mod.path}
      className={`people-hub-module-btn${active ? " active" : ""}`}
      aria-current={active ? "page" : undefined}
      title={opensFullPage ? `${mod.desc} — opens full page` : mod.desc}
    >
      <span className="people-hub-module-icon">
        <Icon size={18} />
      </span>
      <span className="people-hub-module-text">
        <span className="people-hub-module-label">{mod.label}</span>
        <span className="people-hub-module-desc">{mod.desc}</span>
      </span>
      {opensFullPage ? (
        <span className="people-hub-module-go" aria-hidden>
          <ArrowUpRight size={16} />
        </span>
      ) : null}
    </Link>
  );
}

export default function PeopleHubPage() {
  const { role } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [guideOpen, setGuideOpen] = useState(() => {
    try {
      return localStorage.getItem(GUIDE_KEY) !== "1";
    } catch {
      return true;
    }
  });

  const visibleModules = useMemo(
    () => PEOPLE_MODULES.filter((m) => hasRole(role, m.roles)),
    [role]
  );

  const canEmployees = visibleModules.some((m) => m.id === "employees");
  const rawModule = searchParams.get("module");

  if (rawModule === "hierarchy") {
    return <Navigate to={PEOPLE_PATHS.hierarchy} replace />;
  }
  if (rawModule === "orgStructure" || rawModule === "org-structure") {
    return <Navigate to={PEOPLE_PATHS.orgStructure} replace />;
  }
  if (rawModule === "lifecycle") {
    const tab = searchParams.get("tab");
    const qs = tab ? `?tab=${encodeURIComponent(tab)}` : "";
    return <Navigate to={`${PEOPLE_PATHS.lifecycle}${qs}`} replace />;
  }

  if (!canEmployees) {
    const fallback = visibleModules[0];
    if (fallback?.path && fallback.path !== PEOPLE_PATHS.hub) {
      return <Navigate to={fallback.path} replace />;
    }
  }

  const hubView = searchParams.get("view");
  const hubEditId = searchParams.get("id");
  if (hubView === "add") {
    return <Navigate to="/employees/add" replace />;
  }
  if (hubView === "edit" && hubEditId) {
    const next = new URLSearchParams();
    if (searchParams.get("needs_bank") === "1") next.set("needs_bank", "1");
    const step = searchParams.get("step");
    if (step) next.set("step", step);
    const qs = next.toString();
    return <Navigate to={`/employees/${hubEditId}/edit${qs ? `?${qs}` : ""}`} replace />;
  }

  const dismissGuide = () => {
    setGuideOpen(false);
    try {
      localStorage.setItem(GUIDE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="people-hub">
      <header className="people-hub-hero">
        <div className="people-hub-ambient" aria-hidden="true">
          <div className="people-hub-orb people-hub-orb--1" />
          <div className="people-hub-orb people-hub-orb--2" />
        </div>
        <div className="people-hub-hero-inner">
          <div className="people-hub-eyebrow">
            <Sparkles size={11} />
            People
          </div>
          <div className="people-hub-hero-row">
            <h1 className="people-hub-title">People Hub</h1>
            <p className="people-hub-sub">Workforce, reporting lines &amp; lifecycle</p>
          </div>
        </div>

        <nav className="people-hub-modules" aria-label="People modules">
          {visibleModules.map((mod) => (
            <ModuleCard key={mod.id} mod={mod} active={mod.id === "employees"} />
          ))}
        </nav>
      </header>

      {guideOpen && (
        <div className="people-hub-guide">
          <p className="people-hub-guide-text">
            <strong>People Hub:</strong> Employee Management stays here. Reporting Hierarchy and
            Employee Lifecycle each open as a dedicated full page.
          </p>
          <button type="button" className="people-hub-guide-dismiss" onClick={dismissGuide}>
            <X size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
            Got it
          </button>
        </div>
      )}

      <div className="people-hub-content">
        <div className="people-hub-module-panel">
          <EmployeesPage embedded />
        </div>
      </div>
    </div>
  );
}
